/**
 * Reusable Input UI Component
 */

import type { InputHTMLAttributes } from "react";
import { useState, useRef } from "react";
import { format } from "date-fns";
import { CalenderIcon, UpDownIcon } from "@/assets/svg";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  readonly errorMessage?: string;
}

export const Input = ({
  errorMessage,
  className,
  id,
  disabled,
  type,
  value,
  onChange,
  ...restProps
}: InputProps): React.JSX.Element => {
  const helperId = errorMessage ? `${id ?? "input"}-error` : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const baseClasses = "w-full px-3 py-2 rounded-lg border text-sm leading-6 text-gray-900 bg-white transition-all duration-150 ease-in-out placeholder:text-gray-400 focus:outline-none";
  const normalClasses = "border-gray-200 focus:border-[#001a41] focus:shadow-[0_0_0_1px_rgba(0,26,65,0.08)]";
  const errorClasses = "border-red-600";
  const disabledClasses = "bg-gray-100 text-gray-500 cursor-not-allowed";
  
  const isDateInput = type === "date";
  const isNumberInput = type === "number";
  
  // Hide native icons for date and number inputs
  const hideNativeIconsClasses = isDateInput 
    ? "[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
    : isNumberInput 
    ? "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    : "";

  const classes = [
    baseClasses,
    errorMessage ? errorClasses : normalClasses,
    disabled ? disabledClasses : "",
    hideNativeIconsClasses,
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleDateSelect = (date: Date | undefined) => {
    if (date && onChange) {
      const formattedDate = format(date, "yyyy-MM-dd");
      const syntheticEvent = {
        target: { value: formattedDate },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
    setCalendarOpen(false);
  };

  const handleNumberChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || !inputRef.current) return;
    
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const clickY = event.clientY - rect.top;
    const delta = clickY < rect.height / 2 ? 1 : -1;
    
    const currentValue = parseFloat(inputRef.current.value) || 0;
    const step = parseFloat(inputRef.current.step) || 1;
    const min = inputRef.current.min ? parseFloat(inputRef.current.min) : -Infinity;
    const max = inputRef.current.max ? parseFloat(inputRef.current.max) : Infinity;
    
    let newValue = currentValue + (delta * step);
    newValue = Math.max(min, Math.min(max, newValue));
    
    if (onChange) {
      const syntheticEvent = {
        target: { value: newValue.toString() },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const selectedDate = value && typeof value === "string" ? new Date(value) : undefined;

  if (isDateInput) {
    return (
      <div className="flex flex-col gap-1">
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <div className="relative">
              <input
                ref={inputRef}
                id={id}
                type="text"
                readOnly
                value={value && typeof value === "string" ? format(new Date(value), "PPP") : ""}
                className={classes}
                aria-invalid={errorMessage ? true : undefined}
                aria-describedby={helperId}
                disabled={disabled}
                placeholder="Pick a date"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                <CalenderIcon />
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
            />
          </PopoverContent>
        </Popover>
        {errorMessage && (
          <p id={helperId} className="text-xs text-red-600">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          className={classes}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={helperId}
          disabled={disabled}
          min={isNumberInput ? "0" : undefined}
          {...restProps}
        />
        {isNumberInput && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={handleNumberChange}
              onMouseDown={(e) => e.preventDefault()}
              disabled={disabled}
              className="bg-transparent hover:border-none p-0 m-0 flex flex-col items-center justify-center h-10 w-5 cursor-pointer"
              aria-label="Increment/Decrement"
            >
              <UpDownIcon />
            </button>
          </div>
        )}
      </div>
      {errorMessage && (
        <p id={helperId} className="text-xs text-red-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

